// Състоянието на споделената таблица + запомнянето му в localStorage.
//
// Запомнят се НАСТРОЙКИТЕ на потребителя: подредба и видимост на колоните,
// ширини, плътност, брой редове на страница и сортирането. НЕ се запомнят
// филтрите — иначе човек отваря справката утре, вижда празен списък и няма
// как да се сети, че причината е филтър от предишния път.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  ColumnFiltersState,
  ColumnOrderState,
  ColumnSizingState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";

import {
  DATA_TABLE_STATE_VERSION,
  type Density,
  type DataTableStateSnapshot,
} from "./types";

const storageKey = (tableId: string) => `dt.v1.${tableId}`;

/** Прочита запомненото състояние. Всяка грешка → без запомнено състояние. */
const readSnapshot = (tableId: string): DataTableStateSnapshot | null => {
  try {
    const raw = window.localStorage.getItem(storageKey(tableId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DataTableStateSnapshot;
    if (parsed?.version !== DATA_TABLE_STATE_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Запомненият брой редове на страница. В сървърен режим страницата го чете при
 * инициализация, за да пита BE-то веднага с правилния размер, вместо да зареди
 * с default-ния и веднага да презареди.
 */
export const readPersistedPageSize = (
  tableId: string,
  fallback: number,
): number => readSnapshot(tableId)?.pageSize ?? fallback;

/**
 * Запомненото сортиране. В СЪРВЪРЕН режим страницата ЗАДЪЛЖИТЕЛНО го чете при
 * инициализация: таблицата рисува стрелката от запомненото състояние, а
 * заявката тръгва с това, което страницата е сложила в state-а си. Разминат ли
 * се, човек вижда стрелка на „Сума", а списъкът е подреден по час.
 *
 * `allowedIds` пази от 422: колона, преименувана или махната от BE-то, се
 * изхвърля вместо да бъде пратена като `sort_by`.
 */
export const readPersistedSorting = (
  tableId: string,
  fallback: SortingState,
  allowedIds?: readonly string[],
): SortingState => {
  const stored = readSnapshot(tableId)?.sorting;
  if (!stored?.length) return fallback;
  const usable = allowedIds
    ? stored.filter((rule) => allowedIds.includes(rule.id))
    : stored;
  return usable.length ? usable : fallback;
};

/**
 * Запомнената подредба срещу колоните, които справката описва СЕГА. Запомнената
 * може да е от преди колона да бъде добавена, преименувана или махната. Затова:
 * пазим реда на познатите, изхвърляме непознатите, а всяка нова колона влиза
 * ВЕДНАГА СЛЕД съседа си отляво в кода — там, където справката я е сложила,
 * не в края. Без нищо познато — редът от кода.
 */
export const mergeColumnOrder = (
  stored: readonly string[],
  columnIds: readonly string[],
): string[] => {
  const merged = stored.filter((id) => columnIds.includes(id));
  if (!merged.length) return [...columnIds];
  columnIds.forEach((id, index) => {
    if (merged.includes(id)) return;
    // Обхождаме в реда на кода, затова съседът отляво вече е в подредбата:
    // запомнен или вмъкнат на предишната стъпка.
    const insertAt = index === 0 ? 0 : merged.indexOf(columnIds[index - 1]) + 1;
    merged.splice(insertAt, 0, id);
  });
  return merged;
};

type Options = {
  tableId: string;
  /** Всички id-та на колоните в реда, в който справката ги е описала. */
  columnIds: string[];
  initialSorting: SortingState;
  initialPageSize: number;
};

export const useDataTableState = ({
  tableId,
  columnIds,
  initialSorting,
  initialPageSize,
}: Options) => {
  const saved = useRef<DataTableStateSnapshot | null>(null);
  if (saved.current === null) saved.current = readSnapshot(tableId);
  const snapshot = saved.current;

  const [sorting, setSorting] = useState<SortingState>(
    snapshot?.sorting?.length ? snapshot.sorting : initialSorting,
  );
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [density, setDensity] = useState<Density>(snapshot?.density ?? "normal");
  const [pageSize, setPageSize] = useState<number>(
    snapshot?.pageSize ?? initialPageSize,
  );
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
    snapshot?.columnSizing ?? {},
  );

  // Запомнената подредба се слива с колоните от кода (`mergeColumnOrder`) —
  // вместо да изхвърлим цялата подредба заради една нова колона.
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() =>
    mergeColumnOrder(snapshot?.columnOrder ?? [], columnIds),
  );

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const stored = snapshot?.columnVisibility ?? {};
      const next: VisibilityState = {};
      for (const id of columnIds) {
        if (id in stored) next[id] = stored[id];
      }
      return next;
    },
  );

  // Появи ли се нова колона след запомнянето, влиза в подредбата веднага —
  // иначе таблицата би я подала на TanStack извън `columnOrder` и редът ѝ
  // става недетерминиран.
  useEffect(() => {
    setColumnOrder((previous) => {
      const merged = mergeColumnOrder(previous, columnIds);
      const unchanged =
        merged.length === previous.length &&
        merged.every((id, index) => id === previous[index]);
      return unchanged ? previous : merged;
    });
  }, [columnIds]);

  useEffect(() => {
    const payload: DataTableStateSnapshot = {
      version: DATA_TABLE_STATE_VERSION,
      sorting,
      columnOrder,
      columnVisibility,
      columnSizing,
      density,
      pageSize,
    };
    try {
      window.localStorage.setItem(storageKey(tableId), JSON.stringify(payload));
    } catch {
      // Пълен или недостъпен localStorage не бива да чупи справката.
    }
  }, [
    tableId,
    sorting,
    columnOrder,
    columnVisibility,
    columnSizing,
    density,
    pageSize,
  ]);

  const resetLayout = useCallback(() => {
    setColumnOrder(columnIds);
    setColumnVisibility({});
    setColumnSizing({});
    setDensity("normal");
  }, [columnIds]);

  const hasCustomLayout = useMemo(() => {
    const orderChanged = columnOrder.join("|") !== columnIds.join("|");
    const hiddenCount = Object.values(columnVisibility).filter(
      (visible) => visible === false,
    ).length;
    return (
      orderChanged ||
      hiddenCount > 0 ||
      Object.keys(columnSizing).length > 0 ||
      density !== "normal"
    );
  }, [columnOrder, columnIds, columnVisibility, columnSizing, density]);

  return {
    sorting,
    setSorting,
    columnFilters,
    setColumnFilters,
    columnOrder,
    setColumnOrder,
    columnVisibility,
    setColumnVisibility,
    columnSizing,
    setColumnSizing,
    density,
    setDensity,
    pageSize,
    setPageSize,
    resetLayout,
    hasCustomLayout,
  };
};
